import React, { useMemo, useState } from "react";
import { PlusOutlined, EditOutlined, ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Table, Space, Tag, Row, Col, Modal as AntModal, Empty, Input } from "antd";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";
import CreateClinicManager from "./CreateClinicManager";
import ClinicManagerDetails from "./ClinicManagerDetails";
import { canCreateProfessionalUsers } from "../shared/roleUtils";

export default function ClinicManagerMenu() {
  const [isCreate, setIsCreate] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { token } = useToken();
  const url = `${Constants.SERVER_URL}/professional-users`;
  const canCreateProfessionals = useMemo(() => canCreateProfessionalUsers(), []);

  const {
    data: list,
    isLoading,
    error,
    refetch,
  } = useQuery(["clinic_managers"], () =>
    axios.get(url, { headers: { Authorization: "Bearer " + token } }).then((res) =>
      res.data.filter((u) => {
        const roles = Array.isArray(u.roles)
          ? u.roles.map((role) => String(role).toLowerCase())
          : [String(u.role || "").toLowerCase()];
        return roles.includes("clinic_manager");
      })
    )
  );

  const filtered = list?.filter((u) => {
    const s = searchTerm.toLowerCase();
    return (
      u.firstname.toLowerCase().includes(s) ||
      u.lastname.toLowerCase().includes(s) ||
      u.email.toLowerCase().includes(s) ||
      (u.phoneNumber && u.phoneNumber.includes(searchTerm))
    );
  });

  const openModal = (message, isError) => {
    AntModal[isError ? "error" : "success"]({ content: message, okText: "Close", centered: true });
  };

  const handleEdit = (u) => {
    setSelected(u);
    setIsEditMode(true);
  };

  const handleDelete = (u) => {
    AntModal.confirm({
      title: "Supprimer le gestionnaire ?",
      content: `Suppression définitive de ${u.firstname} ${u.lastname}`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk() {
        return axios
          .delete(`${Constants.SERVER_URL}/delete-professional-user/${u.id}`, {
            headers: { Authorization: "Bearer " + token },
          })
          .then(() => {
            refetch();
            openModal("Gestionnaire supprimé", false);
          })
          .catch((err) => openModal(err?.response?.data?.message || "Erreur suppression", true));
      },
    });
  };

  const columns = [
    {
      title: "Nom",
      key: "name",
      render: (_, r) => `${r.firstname} ${r.lastname}`,
    },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Téléphone", dataIndex: "phoneNumber", key: "phoneNumber" },
    {
      title: "Statut",
      key: "active",
      dataIndex: "active",
      render: (active) => <Tag color={active ? "green" : "red"}>{active ? "Actif" : "Inactif"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space size="middle">
          <Button type="link" onClick={() => handleEdit(r)}>
            <EditOutlined /> Modifier
          </Button>
          <Button danger type="link" onClick={() => handleDelete(r)}>
            Supprimer
          </Button>
        </Space>
      ),
    },
  ];

  const renderContent = () => {
    if (isCreate) return <CreateClinicManager refetchClinicManagers={refetch} />;

    if (isEditMode) {
      return (
        <ClinicManagerDetails
          clinicManager={selected}
          onClose={() => {
            setIsEditMode(false);
            setSelected(null);
          }}
          refetchClinicManagers={refetch}
        />
      );
    }

    if (error) {
      return (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <h3>Erreur chargement</h3>
          <Button onClick={() => refetch()}>Réessayer</Button>
        </div>
      );
    }

    return (
      <>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between" }}>
          {canCreateProfessionals ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsCreate(true)}>
              Créer gestionnaire
            </Button>
          ) : (
            <div />
          )}

          <Input
            placeholder="Rechercher"
            prefix={<SearchOutlined />}
            style={{ width: 300, marginTop: 45 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
          />

          {list?.length > 0 && (
            <span>
              Total: {filtered?.length || 0} / {list.length}
            </span>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={isLoading}
          locale={{ emptyText: <Empty description="No clinic managers found" /> }}
          pagination={{ pageSize: 10 }}
        />
      </>
    );
  };

  return (
    <div>
      {(isCreate || isEditMode) && (
        <Row align="middle" justify="space-between" style={{ marginBottom: 20 }}>
          <Col>
            <Button
              onClick={() => {
                setIsCreate(false);
                setIsEditMode(false);
                setSelected(null);
              }}
              type="primary"
              icon={<ArrowLeftOutlined />}
            >
              Retour
            </Button>
          </Col>
          <Col flex="auto" style={{ textAlign: "center" }}>
            <h2 style={{ marginBottom: 0 }}>
              {isCreate ? "Créer un gestionnaire" : "Modifier le gestionnaire"}
            </h2>
          </Col>
          <Col span={4} />
        </Row>
      )}

      {renderContent()}
    </div>
  );
}
